import type { CapabilityEnum } from "@faire/web-api--source/faire/session/CapabilityEnum";
import { MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS } from "@faire/web-api--source/faire/session/CapabilityEnum/MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS";
import { RETAILER_PLACE_ORDER } from "@faire/web-api--source/faire/session/CapabilityEnum/RETAILER_PLACE_ORDER";
import { RETAILER_TRANSFER_OWNERSHIP } from "@faire/web-api--source/faire/session/CapabilityEnum/RETAILER_TRANSFER_OWNERSHIP";
import { IRetailerAccountUserManagement } from "@faire/web-api--source/indigofair/retailer/IRetailerAccountUserManagement";
import { useMemo, useCallback } from "react";

import { useUser } from "./useUser";

export function useUserCapabilities() {
  const { user } = useUser();

  const capabilities = useMemo(() => user?.capabilities ?? [], [user]);

  const userHasSufficientCapability = useCallback(
    (capability: CapabilityEnum): boolean => {
      return capabilities.includes(capability);
    },
    [capabilities]
  );

  const getAccountType = useCallback(() => {
    if (!userHasSufficientCapability(RETAILER_TRANSFER_OWNERSHIP)) {
      return IRetailerAccountUserManagement.Role.OWNER;
    }
    return userHasSufficientCapability(MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS)
      ? IRetailerAccountUserManagement.Role.ADMIN
      : IRetailerAccountUserManagement.Role.CONTRIBUTOR;
  }, [userHasSufficientCapability]);

  return {
    capabilities,
    userHasSufficientCapability,
    isRetailerSubUser: !userHasSufficientCapability(
      RETAILER_TRANSFER_OWNERSHIP
    ),
    isOwnerUser: userHasSufficientCapability(RETAILER_TRANSFER_OWNERSHIP),
    userCanPlaceOrder: userHasSufficientCapability(RETAILER_PLACE_ORDER),
    accountType: getAccountType(),
  };
}
